import { Controller } from "@hotwired/stimulus"
import { track as mixpanelTrack } from "../lib/mixpanel"

// Phase 2.1 Tier 4 — drives the snapshots index + compare UI:
//   - Capture modal → POST /projects/:pid/budgets/:bid/snapshots
//   - Compare modal → GET  /.../snapshots/compare?left=&right=
//   - Delete action → DELETE /.../snapshots/:id with confirm-via-toast
export default class extends Controller {
  static targets = [
    "captureModal", "captureLabel",
    "compareModal", "compareLeftLabel", "compareRight"
  ]

  static values = {
    projectId: Number,
    budgetId: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._pendingCompareLeftId = null
    this._showPendingFlash()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("budget_flash")
    if (!raw) return
    sessionStorage.removeItem("budget_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  stopPropagation(event) { event.stopPropagation() }

  showCaptureModal() {
    this.captureLabelTarget.value = ""
    this._show(this.captureModalTarget)
  }

  hideCaptureModal() { this._hide(this.captureModalTarget) }

  async captureSnapshot() {
    const payload = { budget_snapshot: { label: this.captureLabelTarget.value } }
    const resp = await this._ajaxJSON(
      "POST",
      `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/snapshots`,
      payload
    )
    if (resp.ok) {
      this._track("Budget Snapshot Captured", { budget_snapshot_id: resp.body.id })
      this._flashAndRedirect("Snapshot captured", resp.body.redirect_to)
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to capture snapshot", "error")
    }
  }

  showCompareModal(event) {
    const { snapshotIdParam, snapshotLabelParam } = event.currentTarget.dataset
    this._pendingCompareLeftId = parseInt(snapshotIdParam, 10)
    this.compareLeftLabelTarget.textContent = snapshotLabelParam || `Snapshot #${snapshotIdParam}`
    // Default right side to "live" unless there's another snapshot.
    this.compareRightTarget.value = "live"
    // Disable the "this snapshot" option on the right side.
    Array.from(this.compareRightTarget.options).forEach(opt => {
      opt.disabled = (parseInt(opt.value, 10) === this._pendingCompareLeftId)
    })
    this._show(this.compareModalTarget)
  }

  hideCompareModal() { this._hide(this.compareModalTarget) }

  runCompare() {
    const left = this._pendingCompareLeftId
    const right = this.compareRightTarget.value
    if (!left || !right) {
      this._showToast("Pick a snapshot to compare against", "error")
      return
    }
    const url = `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/snapshots/compare?left=${encodeURIComponent(left)}&right=${encodeURIComponent(right)}`
    this._track("Budget Snapshot Compared", { left_id: left, right_ref: right, via: "modal" })
    window.location.href = url
  }

  async deleteSnapshot(event) {
    const { snapshotIdParam, snapshotLabelParam } = event.currentTarget.dataset
    if (!confirm(`Delete snapshot "${snapshotLabelParam || snapshotIdParam}"? This can't be undone.`)) return

    const resp = await this._ajaxJSON(
      "DELETE",
      `/projects/${this.projectIdValue}/budgets/${this.budgetIdValue}/snapshots/${snapshotIdParam}`
    )
    if (resp.ok) {
      // Drop the row from the DOM rather than reload.
      const row = this.element.querySelector(`tr[data-snapshot-id="${snapshotIdParam}"]`)
      if (row) row.remove()
      this._showToast("Snapshot deleted")
    } else {
      this._showToast(resp.body?.errors?.join(", ") || "Failed to delete snapshot", "error")
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  async _ajaxJSON(method, url, payload) {
    const options = {
      method,
      headers: {
        "X-CSRF-Token": this._csrfToken,
        "Accept": "application/json"
      }
    }
    if (payload !== undefined && method !== "GET") {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(payload)
    }

    const resp = await fetch(url, options)
    let body = null
    try {
      if (resp.headers.get("content-type")?.includes("application/json")) {
        body = await resp.json()
      }
    } catch { /* ignore */ }
    return { ok: resp.ok, status: resp.status, body }
  }

  _show(el) { el.classList.remove("hidden") }
  _hide(el) { el.classList.add("hidden") }

  _track(event, props = {}) {
    try {
      mixpanelTrack(event, {
        project_id: this.projectIdValue,
        budget_id: this.budgetIdValue,
        ...props
      })
    } catch { /* mixpanel optional */ }
  }

  _flashAndRedirect(message, url) {
    sessionStorage.setItem("budget_flash", JSON.stringify({ message, type: "success" }))
    window.location.href = url
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
